import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "@/i18n/LanguageContext";
import { api, formatApiError } from "@/lib/api";
import { IMAGES } from "@/lib/images";
import { Reveal, SectionHeading, CourseCard, Field, inputCls } from "@/components/ui-bits";

const DELIVERY = ["online", "in-person", "hybrid", "on-site"];

export default function Training() {
  const { t } = useLanguage();
  const [params] = useSearchParams();
  const [courses, setCourses] = useState([]);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [delivery, setDelivery] = useState("");
  const [showRequest, setShowRequest] = useState(params.get("request") === "1");
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({});
  const requestTriggerRef = useRef(null);
  const dialogRef = useRef(null);

  const closeRequest = () => {
    setShowRequest(false);
    requestTriggerRef.current?.focus();
  };

  useEffect(() => {
    if (!showRequest) return;
    const timer = setTimeout(() => dialogRef.current?.querySelector("input, textarea, select")?.focus(), 50);
    const onKey = (e) => { if (e.key === "Escape") closeRequest(); };
    window.addEventListener("keydown", onKey);
    return () => { clearTimeout(timer); window.removeEventListener("keydown", onKey); };
  }, [showRequest]);

  useEffect(() => {
    api.get("/courses", { params: { search, category, delivery } })
      .then((r) => setCourses(r.data))
      .catch(() => {});
  }, [search, category, delivery]);

  const categories = useMemo(() => [...new Set(courses.map((c) => c.category).filter(Boolean))], [courses]);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submitRequest = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/course-requests", {
        topic: form.topic || "", objectives: form.objectives || "",
        full_name: form.full_name || "", email: form.email || "",
        phone: form.phone || "", whatsapp: form.whatsapp || "",
        organization: form.organization || "", job_title: form.job_title || "",
        delivery_method: form.delivery_method || "", preferred_dates: form.preferred_dates || "",
        participants: parseInt(form.participants || "1", 10) || 1,
        country: form.country || "", city: form.city || "",
        language: form.language || "", sector: form.sector || "", notes: form.notes || "",
      });
      toast.success(t("req.success"));
      setShowRequest(false);
      setForm({});
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="training-page">
      <section className="hero-grain relative overflow-hidden bg-navy-950 py-24 lg:py-28">
        <img src={IMAGES.keynote} alt="" className="absolute inset-0 h-full w-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-b from-navy-950/50 to-navy-950" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-12">
          <Reveal>
            <p className="mb-4 text-xs font-bold uppercase tracking-[0.3em] text-gold">{t("training.eyebrow")}</p>
            <h1 className="max-w-3xl font-display text-4xl font-semibold text-white sm:text-5xl">{t("page.trainingTitle")}</h1>
            <p className="mt-5 max-w-2xl text-base text-slate-300 sm:text-lg">{t("page.trainingSub")}</p>
            <button
              ref={requestTriggerRef}
              data-testid="request-course-open-button"
              onClick={() => setShowRequest(true)}
              className="mt-8 rounded-full bg-gold px-7 py-3.5 text-sm font-bold text-navy transition-colors hover:bg-gold-hover"
            >
              {t("training.requestCta")}
            </button>
          </Reveal>
        </div>
      </section>

      <section className="py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-12">
          <Reveal className="mb-10 grid gap-4 rounded-lg border border-slate-200 bg-white p-5 shadow-sm sm:grid-cols-3">
            <div className="relative">
              <Search className="absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                data-testid="course-search-input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t("filter.search")}
                className={`${inputCls} ps-9`}
              />
            </div>
            <select data-testid="course-filter-category" value={category} onChange={(e) => setCategory(e.target.value)} className={inputCls}>
              <option value="">{t("filter.category")}: {t("filter.all")}</option>
              {categories.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <select data-testid="course-filter-delivery" value={delivery} onChange={(e) => setDelivery(e.target.value)} className={inputCls}>
              <option value="">{t("filter.delivery")}: {t("filter.all")}</option>
              {DELIVERY.map((d) => <option key={d} value={d}>{t(`delivery.${d}`)}</option>)}
            </select>
          </Reveal>

          {courses.length === 0 ? (
            <p className="py-16 text-center text-slate-500" data-testid="courses-empty">{t("training.empty")}</p>
          ) : (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3" data-testid="courses-grid">
              {courses.map((c) => (
                <Reveal key={c.id}><CourseCard course={c} /></Reveal>
              ))}
            </div>
          )}
        </div>
      </section>

      {showRequest && (
        <div data-testid="request-course-modal" className="fixed inset-0 z-[100] overflow-y-auto bg-navy-950/80 p-4 backdrop-blur-sm" onClick={closeRequest}>
          <div
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="request-course-title"
            className="mx-auto my-10 max-w-2xl rounded-lg bg-white p-8 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id="request-course-title" className="font-display text-3xl font-semibold text-navy">{t("req.title")}</h2>
            <p className="mt-2 text-sm text-slate-600">{t("req.sub")}</p>
            <form onSubmit={submitRequest} className="mt-6 grid gap-4 sm:grid-cols-2" data-testid="course-request-form">
              <div className="sm:col-span-2">
                <Field label={t("req.topic")} required><input required className={inputCls} value={form.topic || ""} onChange={set("topic")} data-testid="req-topic" /></Field>
              </div>
              <div className="sm:col-span-2">
                <Field label={t("req.objectives")}><textarea rows={2} className={inputCls} value={form.objectives || ""} onChange={set("objectives")} data-testid="req-objectives" /></Field>
              </div>
              <Field label={t("form.fullName")} required><input required className={inputCls} value={form.full_name || ""} onChange={set("full_name")} data-testid="req-name" /></Field>
              <Field label={t("form.email")} required><input required type="email" className={inputCls} value={form.email || ""} onChange={set("email")} data-testid="req-email" /></Field>
              <Field label={t("form.phone")}><input className={inputCls} value={form.phone || ""} onChange={set("phone")} data-testid="req-phone" /></Field>
              <Field label={t("form.whatsapp")}><input className={inputCls} value={form.whatsapp || ""} onChange={set("whatsapp")} data-testid="req-whatsapp" /></Field>
              <Field label={t("form.organization")}><input className={inputCls} value={form.organization || ""} onChange={set("organization")} data-testid="req-org" /></Field>
              <Field label={t("form.jobTitle")}><input className={inputCls} value={form.job_title || ""} onChange={set("job_title")} data-testid="req-jobtitle" /></Field>
              <Field label={t("filter.delivery")}>
                <select className={inputCls} value={form.delivery_method || ""} onChange={set("delivery_method")} data-testid="req-delivery">
                  <option value="">—</option>
                  {DELIVERY.map((d) => <option key={d} value={d}>{t(`delivery.${d}`)}</option>)}
                </select>
              </Field>
              <Field label={t("req.dates")}><input className={inputCls} value={form.preferred_dates || ""} onChange={set("preferred_dates")} data-testid="req-dates" /></Field>
              <Field label={t("reg.participants")}><input type="number" min="1" className={inputCls} value={form.participants || ""} onChange={set("participants")} data-testid="req-participants" /></Field>
              <Field label={t("form.country")}><input className={inputCls} value={form.country || ""} onChange={set("country")} data-testid="req-country" /></Field>
              <Field label={t("form.city")}><input className={inputCls} value={form.city || ""} onChange={set("city")} data-testid="req-city" /></Field>
              <Field label={t("req.language")}><input className={inputCls} value={form.language || ""} onChange={set("language")} data-testid="req-language" /></Field>
              <Field label={t("req.sector")}><input className={inputCls} value={form.sector || ""} onChange={set("sector")} data-testid="req-sector" /></Field>
              <div className="sm:col-span-2">
                <Field label={t("form.message")}><textarea rows={2} className={inputCls} value={form.notes || ""} onChange={set("notes")} data-testid="req-notes" /></Field>
              </div>
              <div className="flex gap-3 sm:col-span-2">
                <button type="submit" disabled={sending} data-testid="request-course-submit-button" className="rounded-full bg-gold px-7 py-3 text-sm font-bold text-navy transition-colors hover:bg-gold-hover disabled:opacity-60">
                  {sending ? t("form.sending") : t("req.submit")}
                </button>
                <button type="button" onClick={closeRequest} aria-label="Close" data-testid="request-course-close" className="rounded-full border border-slate-200 px-7 py-3 text-sm font-semibold text-slate-600">
                  ✕
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
